/**
 * TaskDetailPopover -- centered modal popover showing task details.
 *
 * All fields are inline-editable: click to edit, blur/Enter to save.
 * Uses an `editingField` state to track which field is currently being edited.
 */

import { useEffect, useRef, useState } from "react";
import type { SidebarTask } from "../layout/sidebar/types";

interface TaskDetailPopoverProps {
  task: SidebarTask | null;
  isOpen: boolean;
  onClose: () => void;
  onMarkComplete?: (task: SidebarTask) => void;
  onReschedule?: (task: SidebarTask, date: string) => void;
  onDelete?: (task: SidebarTask) => void;
  onUpdateTask?: (id: string, updates: Record<string, unknown>) => void;
  projects?: Array<{ id: string; name: string }>;
}

const STATUS_OPTIONS = ["inbox", "today", "scheduled", "in_progress", "waiting", "done", "archived"];
const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];
const EFFORT_OPTIONS = ["tiny", "small", "medium", "large", "epic"];

const STATUS_COLORS: Record<string, string> = {
  inbox: "bg-surface-active text-text-secondary",
  today: "bg-primary-subtle text-primary",
  scheduled: "bg-[rgba(168,85,247,0.12)] text-[#a855f7]",
  in_progress: "bg-primary-subtle text-primary",
  waiting: "bg-[rgba(234,179,8,0.12)] text-[#eab308]",
  done: "bg-[rgba(74,222,128,0.12)] text-success",
  archived: "bg-surface-active text-text-tertiary",
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: "bg-[rgba(239,68,68,0.12)] text-priority-critical",
  high: "bg-[rgba(249,115,22,0.12)] text-priority-high",
  medium: "bg-[rgba(234,179,8,0.12)] text-priority-medium",
  low: "bg-surface-active text-priority-low",
};

const PRIORITY_TEXT_COLORS: Record<string, string> = {
  critical: "text-priority-critical",
  high: "text-priority-high",
  medium: "text-priority-medium",
  low: "text-priority-low",
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function isOverdue(dateStr: string): boolean {
  const dueDate = new Date(dateStr);
  dueDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
}

type EditingField =
  | null
  | "title"
  | "status"
  | "priority"
  | "dueDate"
  | "project"
  | "effort"
  | "description"
  | "tags"
  | "consequenceOfDelay";

export function TaskDetailPopover({
  task,
  isOpen,
  onClose,
  onMarkComplete,
  onReschedule,
  onDelete,
  onUpdateTask,
  projects,
}: TaskDetailPopoverProps): React.ReactElement {
  const panelRef = useRef<HTMLDivElement>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [editingField, setEditingField] = useState<EditingField>(null);
  const [editValue, setEditValue] = useState("");
  const [tagInput, setTagInput] = useState("");

  // Get tomorrow's date as default
  const getTomorrowDate = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  // Reset state when popover closes
  useEffect(() => {
    if (!isOpen) {
      setShowDatePicker(false);
      setEditingField(null);
      setEditValue("");
      setTagInput("");
    }
  }, [isOpen]);

  // Close on Escape key (only if not editing)
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        if (editingField) {
          setEditingField(null);
          setEditValue("");
        } else {
          onClose();
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, editingField]);

  // Focus trap - focus the panel when opened
  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen || !task) {
    return <></>;
  }

  const canEdit = !!onUpdateTask;

  const startEditing = (field: EditingField, currentValue: string) => {
    if (!canEdit) return;
    setEditingField(field);
    setEditValue(currentValue);
  };

  const saveField = (field: string, value: unknown) => {
    if (onUpdateTask && value !== undefined) {
      onUpdateTask(task.id, { [field]: value });
    }
    setEditingField(null);
    setEditValue("");
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, field: string, value: unknown) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveField(field, value);
    }
    if (e.key === "Escape") {
      setEditingField(null);
      setEditValue("");
    }
  };

  const showOverdue = task.dueDate && task.status !== "done" && task.status !== "archived" && isOverdue(task.dueDate);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-50"
        onClick={onClose}
      />

      {/* Popover panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-sm bg-surface border border-border rounded-xl shadow-lg focus:outline-none"
      >
        {/* Header -- editable title */}
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-border">
          {editingField === "title" ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => {
                if (editValue.trim() && editValue.trim() !== task.title) {
                  saveField("title", editValue.trim());
                } else {
                  setEditingField(null);
                  setEditValue("");
                }
              }}
              onKeyDown={(e) => handleEditKeyDown(e, "title", editValue.trim())}
              className="flex-1 text-sm font-semibold text-text-primary bg-surface border border-border rounded px-2 py-1 focus:outline-none focus:border-primary"
              autoFocus
            />
          ) : (
            <h2
              className={`text-sm font-semibold text-text-primary leading-tight ${canEdit ? "cursor-pointer hover:text-primary" : ""}`}
              onClick={() => startEditing("title", task.title)}
            >
              {task.title}
            </h2>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-text-tertiary hover:text-text-primary hover:bg-surface-hover transition-colors duration-100 flex-shrink-0 -mr-1 -mt-1"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-3 max-h-[50vh] overflow-y-auto">
          {/* Description */}
          <div>
            {editingField === "description" ? (
              <textarea
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => {
                  if (editValue !== (task.description ?? "")) {
                    saveField("description", editValue);
                  } else {
                    setEditingField(null);
                    setEditValue("");
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setEditingField(null);
                    setEditValue("");
                  }
                }}
                rows={3}
                className="w-full text-sm text-text-secondary bg-surface border border-border rounded px-2 py-1 focus:outline-none focus:border-primary resize-none"
                autoFocus
                placeholder="Add a description..."
              />
            ) : (
              <p
                className={`text-sm ${task.description ? "text-text-secondary" : "text-text-tertiary italic"} ${canEdit ? "cursor-pointer hover:text-primary" : ""}`}
                onClick={() => startEditing("description", task.description ?? "")}
              >
                {task.description || (canEdit ? "Add a description..." : "")}
              </p>
            )}
          </div>

          {/* Status + Priority row */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Status */}
            {editingField === "status" ? (
              <select
                value={editValue}
                onChange={(e) => {
                  saveField("status", e.target.value);
                }}
                onBlur={() => { setEditingField(null); setEditValue(""); }}
                className="text-xs bg-surface border border-border rounded px-2 py-1 text-text-secondary focus:outline-none focus:border-primary"
                autoFocus
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s.replace("_", " ")}</option>
                ))}
              </select>
            ) : (
              <span
                className={`font-mono text-xs px-2 py-0.5 rounded capitalize ${STATUS_COLORS[task.status?.toLowerCase() ?? ""] ?? "bg-surface-active text-text-secondary"} ${canEdit ? "cursor-pointer hover:ring-1 hover:ring-primary" : ""}`}
                onClick={() => startEditing("status", task.status ?? "inbox")}
              >
                {task.status?.replace("_", " ") ?? "inbox"}
              </span>
            )}

            {/* Priority */}
            {editingField === "priority" ? (
              <select
                value={editValue}
                onChange={(e) => {
                  saveField("priority", e.target.value);
                }}
                onBlur={() => { setEditingField(null); setEditValue(""); }}
                className="text-xs bg-surface border border-border rounded px-2 py-1 text-text-secondary focus:outline-none focus:border-primary"
                autoFocus
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            ) : (
              <span
                className={`font-mono text-xs px-2 py-0.5 rounded capitalize ${PRIORITY_COLORS[task.priority?.toLowerCase() ?? ""] ?? "bg-surface-active text-text-secondary"} ${canEdit ? "cursor-pointer hover:ring-1 hover:ring-primary" : ""}`}
                onClick={() => startEditing("priority", task.priority ?? "medium")}
              >
                {task.priority ?? "medium"}
              </span>
            )}
          </div>

          {/* Due date */}
          <div className="flex items-center gap-2">
            <svg
              className={`w-4 h-4 ${showOverdue ? "text-error" : "text-text-tertiary"}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {editingField === "dueDate" ? (
              <input
                type="date"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => {
                  if (editValue !== (task.dueDate ?? "")) {
                    saveField("dueDate", editValue || null);
                  } else {
                    setEditingField(null);
                    setEditValue("");
                  }
                }}
                onKeyDown={(e) => handleEditKeyDown(e, "dueDate", editValue || null)}
                className="text-sm bg-surface border border-border rounded px-2 py-1 text-text-primary focus:outline-none focus:border-primary"
                autoFocus
              />
            ) : (
              <span
                className={`text-sm ${showOverdue ? "text-error font-medium" : task.dueDate ? "text-text-secondary" : "text-text-tertiary italic"} ${canEdit ? "cursor-pointer hover:text-primary" : ""}`}
                onClick={() => startEditing("dueDate", task.dueDate ?? "")}
              >
                {task.dueDate ? formatDate(task.dueDate) : (canEdit ? "Set due date" : "No due date")}
                {showOverdue && " (overdue)"}
              </span>
            )}
          </div>

          {/* Project */}
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-text-tertiary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
              />
            </svg>
            {editingField === "project" ? (
              <select
                value={editValue}
                onChange={(e) => {
                  saveField("projectId", e.target.value || null);
                }}
                onBlur={() => { setEditingField(null); setEditValue(""); }}
                className="text-sm bg-surface border border-border rounded px-2 py-1 text-text-secondary focus:outline-none focus:border-primary"
                autoFocus
              >
                <option value="">No project</option>
                {(projects ?? []).map((p) => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>
            ) : (
              <span
                className={`text-sm ${task.project ? "text-text-secondary" : "text-text-tertiary italic"} ${canEdit ? "cursor-pointer hover:text-primary" : ""}`}
                onClick={() => startEditing("project", task.project ?? "")}
              >
                {task.project || (canEdit ? "Set project" : "No project")}
              </span>
            )}
          </div>

          {/* Effort */}
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-text-tertiary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {editingField === "effort" ? (
              <select
                value={editValue}
                onChange={(e) => {
                  saveField("effort", e.target.value || null);
                }}
                onBlur={() => { setEditingField(null); setEditValue(""); }}
                className="text-sm bg-surface border border-border rounded px-2 py-1 text-text-secondary focus:outline-none focus:border-primary"
                autoFocus
              >
                <option value="">No effort set</option>
                {EFFORT_OPTIONS.map((e) => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            ) : (
              <span
                className={`text-sm ${task.effort ? "text-text-secondary" : "text-text-tertiary italic"} ${canEdit ? "cursor-pointer hover:text-primary" : ""}`}
                onClick={() => startEditing("effort", task.effort ?? "")}
              >
                {task.effort || (canEdit ? "Set effort" : "No effort")}
              </span>
            )}
          </div>

          {/* Tags */}
          <div>
            <div className="flex flex-wrap gap-1 items-center">
              {(task.tags ?? []).map((tag) => (
                <span
                  key={tag}
                  className="bg-surface-active text-text-secondary text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                >
                  {tag}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => {
                        const newTags = (task.tags ?? []).filter(t => t !== tag);
                        onUpdateTask!(task.id, { tags: newTags });
                      }}
                      className="text-text-tertiary hover:text-error"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </span>
              ))}
              {canEdit && (
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && tagInput.trim()) {
                      e.preventDefault();
                      const newTags = [...(task.tags ?? []), tagInput.trim()];
                      onUpdateTask!(task.id, { tags: newTags });
                      setTagInput("");
                    }
                  }}
                  placeholder="Add tag..."
                  className="text-xs bg-transparent border-none outline-none text-text-tertiary placeholder:text-text-tertiary w-20"
                />
              )}
            </div>
          </div>

          {/* AI Priority */}
          {task.aiPriority && (
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
              </svg>
              <span className="text-sm text-text-secondary">
                AI suggests <span className={`font-medium capitalize ${PRIORITY_TEXT_COLORS[task.aiPriority.toLowerCase()] ?? "text-text-primary"}`}>{task.aiPriority}</span> priority
              </span>
            </div>
          )}

          {/* Procrastination warning */}
          {task.reschedulingCount != null && task.reschedulingCount >= 2 && (
            <div className={`flex items-start gap-2 px-3 py-2 rounded-lg ${task.reschedulingCount >= 3 ? "bg-error/8 border border-error/20" : "bg-warning/8 border border-warning/20"}`}>
              <svg className={`w-4 h-4 flex-shrink-0 mt-0.5 ${task.reschedulingCount >= 3 ? "text-error" : "text-warning"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <p className={`text-xs ${task.reschedulingCount >= 3 ? "text-error" : "text-warning"}`}>
                Rescheduled {task.reschedulingCount} times{task.reschedulingCount >= 3 && " — consider breaking this into smaller steps"}
              </p>
            </div>
          )}

          {/* Consequence of delay */}
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {editingField === "consequenceOfDelay" ? (
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => {
                  if (editValue !== (task.consequenceOfDelay ?? "")) {
                    saveField("consequenceOfDelay", editValue || null);
                  } else {
                    setEditingField(null);
                    setEditValue("");
                  }
                }}
                onKeyDown={(e) => handleEditKeyDown(e, "consequenceOfDelay", editValue || null)}
                className="flex-1 text-xs text-warning bg-surface border border-border rounded px-2 py-1 focus:outline-none focus:border-primary"
                autoFocus
                placeholder="What happens if delayed?"
              />
            ) : (
              <div
                className={canEdit ? "cursor-pointer hover:text-primary" : ""}
                onClick={() => startEditing("consequenceOfDelay", task.consequenceOfDelay ?? "")}
              >
                {task.consequenceOfDelay ? (
                  <>
                    <span className="text-xs font-medium text-text-secondary">If delayed: </span>
                    <span className="text-xs text-warning">{task.consequenceOfDelay}</span>
                  </>
                ) : canEdit ? (
                  <span className="text-xs text-text-tertiary italic">Set consequence of delay...</span>
                ) : null}
              </div>
            )}
          </div>

          {/* AI Notes */}
          {task.aiNotes && (
            <p className="text-xs text-text-tertiary italic border-l-2 border-border pl-2">
              {task.aiNotes}
            </p>
          )}
        </div>

        {/* Actions footer */}
        {(onMarkComplete || onReschedule || onDelete) && (
          <div className="flex flex-col gap-2 px-4 py-3 border-t border-border">
            {/* Primary actions row */}
            {task.status !== "done" && task.status !== "archived" && (onMarkComplete || onReschedule) && (
              <div className="flex items-center gap-2">
                {onMarkComplete && (
                  <button
                    type="button"
                    onClick={() => {
                      onMarkComplete(task);
                      onClose();
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-success bg-[rgba(74,222,128,0.08)] hover:bg-[rgba(74,222,128,0.15)] rounded-lg transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Complete
                  </button>
                )}
                {onReschedule && (
                  showDatePicker ? (
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="date"
                        defaultValue={getTomorrowDate()}
                        min={getTomorrowDate()}
                        className="flex-1 text-xs bg-surface border border-border rounded-lg px-3 py-2 text-text-primary"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            setShowDatePicker(false);
                          } else if (e.key === "Enter") {
                            const date = (e.target as HTMLInputElement).value;
                            if (date) {
                              onReschedule(task, date);
                              onClose();
                            }
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = panelRef.current?.querySelector('input[type="date"]') as HTMLInputElement;
                          if (input?.value) {
                            onReschedule(task, input.value);
                            onClose();
                          }
                        }}
                        className="px-3 py-2 text-xs font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowDatePicker(true)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-primary bg-primary-subtle hover:bg-primary/20 rounded-lg transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      Reschedule
                    </button>
                  )
                )}
              </div>
            )}
            {/* Delete action */}
            {onDelete && (
              <button
                type="button"
                onClick={() => {
                  if (confirm(`Delete "${task.title}"?`)) {
                    onDelete(task);
                    onClose();
                  }
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-error hover:bg-[rgba(239,68,68,0.08)] rounded-lg transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
